import { useRef, useContext, useState, useEffect } from 'react';
import { Box, IconButton, useTheme, useMediaQuery } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

import { GlobalContext } from '../contexts/global-context';
import { ModelPreviewCard } from './model-preview-card';
import { ModelCardWrapper } from './model-card-wrapper';

export function ModelCarousel({ 
    models, 
    cardWidth = 300,
    gap = 16,
    showArrows = true,
    onModelClick = null,
    visibleCards = { xs: 1, sm: 2, md: 3, lg: 4, xl: 4 },
    enableWrap = true,
    showPreview = true,
    onAllImagesLoaded = null,
    hideIndividualLoading = false
}) {
    const { isMobile } = useContext(GlobalContext);
    const theme = useTheme();
    const scrollRef = useRef(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const loadedCardsRef = useRef(new Set());
    
    // Responsive breakpoint logic
    const isXs = useMediaQuery(theme.breakpoints.only('xs'));
    const isSm = useMediaQuery(theme.breakpoints.only('sm'));
    const isMd = useMediaQuery(theme.breakpoints.only('md'));
    const isLg = useMediaQuery(theme.breakpoints.only('lg'));
    const isXl = useMediaQuery(theme.breakpoints.up('xl'));
    
    // Determine cards to show based on breakpoint
    const getVisibleCards = () => {
        if (isXs) return visibleCards.xs || 1;
        if (isSm) return visibleCards.sm || 2;
        if (isMd) return visibleCards.md || 3;
        if (isLg) return visibleCards.lg || 4;
        if (isXl) return visibleCards.xl || 5;
        return visibleCards.md || 3; // Fallback
    };
    
    const maxVisible = getVisibleCards();
    const totalCards = models.length;
    
    // Handle individual card image loading
    const handleCardImageLoaded = (modelName) => {
        loadedCardsRef.current.add(modelName);
        
        // Check if all cards are loaded and notify parent
        if (loadedCardsRef.current.size === models.length && onAllImagesLoaded) {
            onAllImagesLoaded();
        }
    };
    
    // Reset loaded cards when models change
    useEffect(() => {
        loadedCardsRef.current = new Set();
    }, [models]);
    
    // Calculate container width based on maximum possible cards for consistent sizing
    const containerWidth = maxVisible * cardWidth + (maxVisible - 1) * gap;
    
    // Calculate actual cards to show for scrolling logic
    const cardsToShow = Math.min(maxVisible, totalCards);

    const scroll = (direction) => {
        if (direction === 'left') {
            setCurrentIndex(prev => {
                if (enableWrap) {
                    return prev === 0 ? totalCards - 1 : prev - 1;
                }
                return Math.max(0, prev - 1);
            });
        } else {
            setCurrentIndex(prev => {
                if (enableWrap) {
                    return (prev + 1) % totalCards;
                }
                return Math.min(totalCards - 1, prev + 1);
            });
        }
    };
    
    // Update scroll position when currentIndex changes
    useEffect(() => {
        if (scrollRef.current) {
            const scrollAmount = currentIndex * (cardWidth + gap);
            scrollRef.current.scrollTo({
                left: scrollAmount,
                behavior: 'smooth'
            });
        }
    }, [currentIndex, cardWidth, gap]);

    const canScrollLeft = () => {
        if (totalCards <= cardsToShow) return false; // No scrolling needed
        return enableWrap || currentIndex > 0;
    };

    const canScrollRight = () => {
        if (totalCards <= cardsToShow) return false; // No scrolling needed
        return enableWrap || currentIndex < totalCards - cardsToShow;
    };

    return (
        <Box sx={{ 
            position: 'relative', 
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
        }}>
            {/* Left Arrow */}
            {showArrows && (
                <IconButton
                    onClick={() => scroll('left')}
                    disabled={!canScrollLeft()}
                    sx={{
                        position: 'absolute',
                        left: isMobile ? 8 : 16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.common.white,
                        boxShadow: 3,
                        width: isMobile ? 44 : 56,
                        height: isMobile ? 44 : 56,
                        '&:hover': {
                            backgroundColor: theme.palette.primary.dark,
                            transform: 'translateY(-50%) scale(1.1)',
                        },
                        '&:disabled': {
                            opacity: 0.3,
                            backgroundColor: theme.palette.action.disabled,
                        },
                        transition: 'all 0.2s ease-in-out'
                    }}
                >
                    <ArrowBackIosIcon fontSize={isMobile ? "small" : "medium"} />
                </IconButton>
            )}

            {/* Carousel Container */}
            <Box sx={{
                width: containerWidth,
                position: 'relative',
                display: 'flex',
                justifyContent: totalCards < maxVisible ? 'center' : 'flex-start'
            }}>
                <Box
                    ref={scrollRef}
                    sx={{
                        display: 'flex',
                        gap: `${gap}px`,
                        overflowX: 'hidden',
                        scrollBehavior: 'smooth',
                        width: totalCards < maxVisible ? 'auto' : totalCards * (cardWidth + gap) - gap,
                        // Hide scrollbar
                        '&::-webkit-scrollbar': {
                            display: 'none',
                        },
                        msOverflowStyle: 'none',
                        scrollbarWidth: 'none',
                    }}
                >
                    {models.map((model) => (
                        <Box
                            key={model.name}
                            sx={{
                                flexShrink: 0,
                                width: cardWidth
                            }}
                        >
                            <ModelCardWrapper inCarousel={true}>
                                <ModelPreviewCard
                                    model={model}
                                    width={cardWidth}
                                    showOverlay={!isMobile}
                                    showPreview={showPreview}
                                    onClick={onModelClick}
                                    onImageLoaded={() => handleCardImageLoaded(model.name)}
                                    hideLoadingWheel={hideIndividualLoading}
                                />
                            </ModelCardWrapper>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Right Arrow */}
            {showArrows && (
                <IconButton
                    onClick={() => scroll('right')}
                    disabled={!canScrollRight()}
                    sx={{
                        position: 'absolute',
                        right: isMobile ? 8 : 16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.common.white,
                        boxShadow: 3,
                        width: isMobile ? 44 : 56,
                        height: isMobile ? 44 : 56,
                        '&:hover': {
                            backgroundColor: theme.palette.primary.dark,
                            transform: 'translateY(-50%) scale(1.1)',
                        },
                        '&:disabled': {
                            opacity: 0.3,
                            backgroundColor: theme.palette.action.disabled,
                        },
                        transition: 'all 0.2s ease-in-out'
                    }}
                >
                    <ArrowForwardIosIcon fontSize={isMobile ? "small" : "medium"} />
                </IconButton>
            )}
        </Box>
    );
}